import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * FlyerPage — a print-optimized 8.5"x11" flyer Michael can drop off at
 * trucking companies he already knows. Renders at /flyer.
 *
 * How to use:
 *   1. Open https://freightpilotai.ai/flyer (or /flyer?loc=acme for tracking)
 *   2. Ctrl+P (or Cmd+P) → Save as PDF, or print direct to paper
 *   3. Drop off at the front desk / driver break room / dispatcher's office
 *
 * Query params:
 *   loc=acme       — tags the QR code URL so we know which drop-off converted
 *                    (e.g. utm_source=flyer&utm_campaign=acme)
 *   phone=6051234  — override the printed callback number if needed
 *
 * The @media print styles hide the "Print this flyer" instruction bar so
 * the printed page is JUST the flyer content, no chrome.
 */
export default function FlyerPage() {
  const [params] = useSearchParams();
  const loc = (params.get("loc") || "trucking").trim().toLowerCase();
  const callbackPhone = params.get("phone") || "(605) 679-7303";

  const qrTargetUrl =
    `https://freightpilotai.ai/contact?intent=concierge-setup` +
    `&utm_source=flyer&utm_medium=print&utm_campaign=${encodeURIComponent(loc)}`;

  // Public QR generator — no auth, no cost, high-contrast black-on-white
  // so it scans reliably even on a smudged printout.
  const qrImgSrc =
    `https://api.qrserver.com/v1/create-qr-code/?` +
    `size=380x380&margin=8&ecc=H&data=${encodeURIComponent(qrTargetUrl)}`;

  // Set document title so print PDF filename is useful
  useEffect(() => {
    document.title = `FreightPilotAI — Drop-off Flyer (${loc})`;
  }, [loc]);

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Print-only styles — @page controls PDF margins */}
      <style>{`
        @page { size: Letter portrait; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: #fff !important; }
          .flyer-sheet { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      {/* Instruction bar — only visible on screen, hidden on print */}
      <div className="no-print bg-neutral-900 text-white text-sm py-3 px-6 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <b>Ready to print.</b> Press <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Ctrl+P</kbd> (Windows) or <kbd className="px-1.5 py-0.5 bg-neutral-700 rounded text-xs">Cmd+P</kbd> (Mac). Choose "Save as PDF" or print to paper on 8.5"×11".
        </div>
        <button
          onClick={() => window.print()}
          className="bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-semibold rounded-md px-4 py-2 text-sm shrink-0"
          data-testid="flyer-print-btn"
        >
          Print now
        </button>
      </div>

      {/* Flyer content — Letter size at 96dpi ≈ 816×1056px, we render slightly
          narrower and center it. Print @page will lay this out full-bleed. */}
      <div
        className="flyer-sheet mx-auto my-6 bg-white shadow-lg"
        style={{ width: "8.5in", minHeight: "11in", padding: "0.6in 0.65in" }}
        data-testid="flyer-sheet"
      >
        {/* ─── Top brand strip ─── */}
        <div className="flex items-center justify-between border-b-2 border-[#FF5A1F] pb-4 mb-6">
          <div className="flex items-baseline gap-1">
            <span className="font-black text-2xl tracking-tight">Freight</span>
            <span className="font-black text-2xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-2xl tracking-tight">AI</span>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">Made in</div>
            <div className="text-sm font-bold">Sioux Falls, SD 🇺🇸</div>
          </div>
        </div>

        {/* ─── Big pain-point headline ─── */}
        <h1 className="text-[46px] leading-[1.02] font-black tracking-tight mb-3">
          Stop losing <span className="text-[#FF5A1F]">12 hours a week</span>
          <br />to office paperwork.
        </h1>
        <p className="text-lg text-neutral-700 leading-snug mb-8 max-w-[6.2in]">
          Automated dispatch, invoicing, driver payroll, and broker back-and-forth — <b>one dashboard</b> replaces the 4 apps your ops team is duct-taping together. Small fleets save <b>~$2,400 a month</b> in underpriced loads.
        </p>

        {/* ─── 3-point outcome list ─── */}
        <div className="grid grid-cols-3 gap-5 mb-8">
          <OutcomeCard number="12+" unit="hrs/week saved" caption="on dispatch paperwork" />
          <OutcomeCard number="4→1" unit="apps replaced" caption="one dashboard for it all" />
          <OutcomeCard number="$2,400" unit="reclaimed monthly" caption="from underpriced loads" />
        </div>

        {/* ─── THE OFFER — the whole reason for the flyer ─── */}
        <div className="rounded-xl border-2 border-[#FF5A1F] bg-[#FFF3EC] p-6 mb-6">
          <div className="flex items-baseline gap-3 mb-2">
            <div className="text-[9px] uppercase tracking-widest bg-[#FF5A1F] text-black font-bold px-2 py-1 rounded">
              For {loc === "trucking" ? "small fleet owners" : loc}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">
              Limited to 5 fleets · 7-day free trial
            </div>
          </div>
          <h2 className="text-[32px] leading-[1.05] font-black tracking-tight mb-3">
            I'll set it up <span className="text-[#FF5A1F]">for you</span>. Free.
          </h2>
          <p className="text-[15px] text-neutral-800 leading-snug mb-3">
            Book a <b>30-min screen share</b> and I (Michael, the founder) will personally configure your dispatch, invoicing, and driver comms while you watch. You start a <b>7-day free trial</b> (card on file, $0 today). If you like it, it just keeps running. If not, cancel before day 8 — you keep the setup free.
          </p>
          <ul className="text-[14px] text-neutral-800 space-y-1 leading-snug">
            <li>✅ &nbsp;<b>$0 today</b> — cancel in one tap before day 8</li>
            <li>✅ &nbsp;<b>I do all the work</b> — you don't touch a keyboard</li>
            <li>✅ &nbsp;<b>Zero commitment</b> — if you say no, we're still cool</li>
          </ul>
        </div>

        {/* ─── QR CODE + Callback CTA ─── */}
        <div className="flex items-center gap-6 border-t-2 border-neutral-900 pt-6">
          <img
            src={qrImgSrc}
            alt="Scan to book your free setup"
            width={170}
            height={170}
            className="shrink-0 border border-neutral-300"
            data-testid="flyer-qr"
          />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
              Scan the QR or…
            </div>
            <div className="text-[22px] font-black leading-tight mb-2">
              Text or call <span className="text-[#FF5A1F]">{callbackPhone}</span>
            </div>
            <div className="text-[14px] text-neutral-700 leading-snug mb-3">
              Say <i>"I saw your flyer"</i> and I'll get you a 30-min slot this week.
            </div>
            <div className="text-[13px] text-neutral-800 border-l-4 border-[#FF5A1F] pl-3 italic leading-snug">
              "I'm a solo founder in Sioux Falls building this for small fleet owners like you. I want to earn your trust the old-school way — by doing the work myself and letting the software prove itself."
              <div className="not-italic text-[12px] text-neutral-600 mt-1.5 font-semibold">
                — Michael Guley, Founder
              </div>
            </div>
          </div>
        </div>

        {/* ─── Bottom URL footer ─── */}
        <div className="mt-6 pt-4 border-t border-neutral-300 flex items-center justify-between text-[11px] text-neutral-500">
          <div>
            <b className="text-neutral-900">freightpilotai.ai</b> · Purpose-built for owner-operators & fleets under 20 trucks
          </div>
          <div className="uppercase tracking-widest">
            Print &amp; drop off · {loc}
          </div>
        </div>
      </div>

      {/* Screen-only footer with quick tracking-URL preview */}
      <div className="no-print max-w-2xl mx-auto my-8 p-4 rounded-lg border border-neutral-200 bg-neutral-50 text-xs text-neutral-600">
        <div className="font-semibold text-neutral-900 mb-1">💡 Pro tip: track which drop-off converted</div>
        <div className="mb-2">Add <code className="bg-white border px-1 rounded">?loc=companyname</code> to the URL when printing a batch. Each variant's QR code carries a unique <code className="bg-white border px-1 rounded">utm_campaign</code> so you can see in Analytics which fleet actually scanned.</div>
        <div>Examples:</div>
        <ul className="mt-1 space-y-0.5 font-mono text-[11px]">
          <li>/flyer?loc=acme-trucking</li>
          <li>/flyer?loc=johnson-fleet</li>
          <li>/flyer?loc=oreilly-transport</li>
        </ul>
        <div className="mt-2">Currently tracking as: <code className="bg-white border px-1 rounded">utm_campaign={loc}</code></div>
      </div>
    </div>
  );
}

function OutcomeCard({ number, unit, caption }) {
  return (
    <div className="rounded-lg border-2 border-neutral-900 p-4">
      <div className="text-[32px] font-black leading-none text-[#FF5A1F] tabular-nums">
        {number}
      </div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-900 font-bold mt-1">
        {unit}
      </div>
      <div className="text-[12px] text-neutral-600 mt-1 leading-tight">
        {caption}
      </div>
    </div>
  );
}
